import EcranInstructions from "@/modules/autres/EcranInstructions";
import { useState, useEffect, useRef, useCallback } from "react";
import { useFullscreen } from "@mantine/hooks";
import Bilan, { PositionBilan } from "../bilan/Bilan";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import EcranInstructionsPreset from "../autres/EcranInscructionsPreset";
import { computeVisibleGrid } from "@/utils/computeVisibleGrid";
import { updateSelectionBilan } from "@/utils/updateSelectionBilan";

export interface ResultBilan {
  position: string;
  reactionTime: number | string;
}

export const LauncherBilan = ({
  onModuleEnd,
  role,
  startSession,
  currentModuleIndex,
  saveFieldSession,
  status,
  targetEnCours_Zf_Bilan,
  resultsBilan,
}: {
  onModuleEnd: () => void;
  role: "practitioner" | "patient";
  startSession: () => void;
  currentModuleIndex: number;
  saveFieldSession: (
    fieldsToUpdate: {
      field: string;
      value: string | number | number[] | Date | null;
    }[]
  ) => void;
  status: "EN_COURS" | "PREVUE" | "REALISEE";
  targetEnCours_Zf_Bilan: PositionBilan[] | null;
  resultsBilan: ResultBilan[] | null;
}) => {
  const [moduleActif, setModuleActif] = useState(false);
  const { toggle } = useFullscreen();
  const isPausedAndNotFullScreenRef = useRef<boolean>(
    !document.fullscreenElement
  ); // Initialize with the current fullscreen state
  const [isPaused, setIsPaused] = useState(true);
  const bilanRef = useRef<{ start: () => void }>(null);
  const [finalResults, setFinalResults] = useState<ResultBilan[]>([]);
  const lancerModule = useCallback(() => {
    setModuleActif(true);
    setIsPaused(false);
    // toggle();
  }, []);

  const { patientId } = useParams<{ patientId: string }>();

  const caseSize = localStorage.getItem("selectedCaseSizePx");

  useEffect(() => {
    const handleFullscreenChange = () => {
      isPausedAndNotFullScreenRef.current = !document.fullscreenElement;
    };

    const handleKeyPress = async (event: { key: string }) => {
      if ((event.key === "p" || event.key === "Escape") && !isPaused) {
        toggle();
        isPausedAndNotFullScreenRef.current =
          !isPausedAndNotFullScreenRef.current;
      } else if (event.key === " ") {
        if (bilanRef.current) {
          bilanRef.current.start();
        }
      }
    };

    window.addEventListener("fullscreenchange", handleFullscreenChange);
    window.addEventListener("keydown", handleKeyPress);

    const maxCanvasWidth = window.innerWidth;
    const maxCanvasHeight = window.innerHeight;

    const { removedColumns, removedRows } = computeVisibleGrid(
      Number(caseSize),
      maxCanvasWidth,
      maxCanvasHeight
    );
    if (finalResults.length !== 0) {
      try {
        const fieldsToUpdate = [
          { field: "sessionDate", value: new Date() },
          { field: "resultsBilan", value: JSON.stringify(finalResults) },
          { field: "removedColumns", value: removedColumns },
          { field: "removedRows", value: removedRows },
          { field: "targetEnCours_Zf_Bilan", value: null },
        ];

        saveFieldSession(fieldsToUpdate);

        toast.success("Le bilan a bien été enregistré");
        updateSelectionBilan(role, finalResults, patientId);
        onModuleEnd();
      } catch (error) {
        console.error("Erreur lors de l'enregistrement du bilan:", error);
        toast.error("Erreur lors de l'enregistrement du bilan", {
          position: "top-center",
          autoClose: 2000,
        });
      }
    }

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      window.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [
    moduleActif,
    isPausedAndNotFullScreenRef,
    toggle,
    finalResults,
    isPaused,
    patientId,
    lancerModule,
  ]);

  if (
    !caseSize ||
    caseSize === "undefined" ||
    caseSize === "null" ||
    caseSize === null ||
    Number(caseSize) === 0
  ) {
    return (
      <EcranInstructions instructions="Veuillez sélectionner une taille de case dans les paramètres avant de commencer le test." />
    );
  }

  return (
    <div>
      {!moduleActif ? (
        <EcranInstructionsPreset
          options={{
            dynamicDisplay: true,
            fontSizeMultiplier: 1.5,
            textColor: "white",
          }}
          preset={"bilan-champ-visuel"}
          setShowInstructions={lancerModule}
          startSession={startSession}
          currentModuleIndex={currentModuleIndex}
        />
      ) : (
        <Bilan
          ref={bilanRef}
          caseSize={Number(caseSize)}
          isPaused={isPaused}
          setIsPaused={setIsPaused}
          setFinalResults={setFinalResults}
          toggleFullScreen={toggle}
          saveFieldSession={saveFieldSession}
          status={status}
          targetEnCours_Zf_Bilan={targetEnCours_Zf_Bilan}
          resultsBilan={resultsBilan}
        />
      )}
    </div>
  );
};
